import { IconButton, Tooltip } from "@mui/material";
import { Page } from "@mb-pro-ui/components";
import {
  EnhancedTable,
  FilterView as TableFilterView,
  Filter as TableFilter,
  RemoteFilterViews,
  SimpleBooleanFilter,
  SimpleTextFilter,
} from "@mb-pro-ui/components/table";
import { Firmware, FirmwareFilter } from "../utils/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Column, Row } from "react-table";
import { defineMessage, FormatDateOptions, useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import { useErrorHandler } from "../hooks/useErrorHandler";
import { useMutation } from "react-query";
import { useApi } from "../utils/ApiProvider";
import { isEqual } from "lodash-es";
import { useFirmwareProperties } from "../hooks/useFirmwareProperties";
import { usePageSxProps } from "./styles";
import { APP_NAME } from "../App";

export const FirmwaresPage = () => {
  const [filter, setFilter] = useState<FirmwareFilter>({});

  const { listPageSx } = usePageSxProps();
  const { formatMessage, formatDate } = useIntl();
  const history = useHistory();
  const api = useApi();
  const { statusValues, urgencyValues } = useFirmwareProperties();

  const dateFormatOptions: FormatDateOptions = useMemo(
    () => ({
      dateStyle: "short",
      timeStyle: "medium",
    }),
    []
  );

  // TODO: többi előfordulható hiba
  const { errorHandler: onError } = useErrorHandler([
    [
      { status: 401, type: "firmwares" },
      () => formatMessage({ defaultMessage: "You have no rights for firmware management." }),
      () => {
        history.push("/");
      },
    ]
  ]);

  const firmwaresQuery = useCallback((filter: FirmwareFilter) => api<Firmware[]>("/firmwares/list", { method: "post", body: JSON.stringify(filter) }), [api]);
  const { mutate: getFirmwares, data: files, status: queryStatus } = useMutation({ mutationFn: firmwaresQuery, onError });

  const columns = useMemo(
    (): Column<Firmware>[] => [
      {
        id: "hwtype",
        Header: formatMessage({
          defaultMessage: "Hardware type",
        }),
        accessor: (item) => item.hwtype,
        width: 75,
      },
      {
        id: "swtype",
        Header: formatMessage({
          defaultMessage: "Software type",
        }),
        accessor: (item) => item.swtype ?? "",
        width: 75,
      },
      {
        id: "hwver",
        Header: formatMessage({
          defaultMessage: "Hardware",
        }),
        accessor: (item) => item.hwver,
        width: 30,
      },
      {
        id: "swver",
        Header: formatMessage({
          defaultMessage: "Software",
        }),
        accessor: (item) => item.swver,
        width: 30,
      },
      {
        id: "description",
        Header: formatMessage({
          defaultMessage: "Description",
        }),
        accessor: (item) => item.description ?? "",
        width: 75,
      },
      {
        id: "status",
        Header: formatMessage({
          defaultMessage: "Status",
        }),
        accessor: (item) => statusValues.get(item.status),
        width: 30,
      },
      {
        id: "urgency",
        Header: formatMessage({
          defaultMessage: "Urgency",
        }),
        accessor: (item) => urgencyValues.get(item.urgency),
        width: 30,
      },
      {
        id: "lastChanged",
        Header: formatMessage({
          defaultMessage: "Last changed",
        }),
        accessor: (item) => formatDate(item.last_changed_ts, dateFormatOptions),
        width: 50,
      },
    ],
    [dateFormatOptions, formatDate, formatMessage, statusValues, urgencyValues]
  );

  const tableFilters = useMemo(
    (): TableFilterView[] => [
      {
        id: "hwtype",
        Filter: SimpleTextFilter({ label: formatMessage({ defaultMessage: "Hardware type" }) }),
      },
      {
        id: "hwver",
        Filter: SimpleTextFilter({ label: formatMessage({ defaultMessage: "Hardware version" }) }),
      },
      {
        id: "swtype",
        Filter: SimpleTextFilter({ label: formatMessage({ defaultMessage: "Software type" }) }),
      },
      {
        id: "swver",
        Filter: SimpleTextFilter({ label: formatMessage({ defaultMessage: "Software version" }) }),
      },
      {
        id: "description",
        Filter: SimpleTextFilter({ label: formatMessage({ defaultMessage: "Description" }) }),
      },
      {
        id: "include-deleted",
        Filter: SimpleBooleanFilter(defineMessage({ defaultMessage: "Include deleted firmwares" }), null, "hideFalse"),
      },
    ],
    [formatMessage]
  );

  const filterChanged = useCallback((filter: TableFilter[]) => {
    setFilter((old) => {
      const newFilter = {
        status: [-1, 0, 1, 2, 3],
      } as FirmwareFilter;
      filter.forEach(({ id, value }) => {
        if (id === "hwtype" && value) {
          newFilter.hwtype = value;
        }
        if (id === "hwver" && value) {
          newFilter.hwver = parseInt(value);
        }
        if (id === "swtype" && value) {
          newFilter.swtype = value;
        }
        if (id === "swver" && value) {
          newFilter.swver = parseInt(value);
        }
        if (id === "description" && value) {
          newFilter.description = value;
        }
        if (id === "include-deleted" && value) {
          delete newFilter.status;
        }
      });
      return isEqual(old, newFilter) ? old : newFilter;
    });
  }, []);

  const newFirmware = useCallback(() => {
    history.push("/firmwares/new");
  }, [history]);

  const updateFirmwares = useCallback(() => getFirmwares(filter), [getFirmwares, filter]);

  useEffect(() => {
    document.title = `${formatMessage({ defaultMessage: "Firmwares" })} - ${APP_NAME}`;
  }, [formatMessage]);

  useEffect(() => {
    updateFirmwares();
  }, [updateFirmwares]);

  return (
    <Page sx={listPageSx}>
      <EnhancedTable
        columns={columns}
        data={files ?? []}
        showResultCount
        // getRowProps={rowProps}
        queryStatus={queryStatus}
        overscanCount={5}
        onRowClick={(item: Row<Firmware>) => history.push(`/firmwares/${item.original.id}`)}
        postfix={
          <>
            <IconButton
              size="small"
              sx={{
                color: "primary.contrastText",
              }}
              onClick={updateFirmwares}
            >
              <Tooltip
                title={formatMessage({
                  defaultMessage: "Reload",
                })}
              >
                <RefreshIcon />
              </Tooltip>
            </IconButton>
            <IconButton
              size="small"
              sx={{
                color: "primary.contrastText",
              }}
              onClick={newFirmware}
            >
              <Tooltip
                title={formatMessage({
                  defaultMessage: "Create new file",
                })}
              >
                <AddIcon />
              </Tooltip>
            </IconButton>
          </>
        }
        filters={(opts) => <RemoteFilterViews {...opts} tableFilters={tableFilters} onFilterChange={filterChanged} />}
        limit={1000}
      />
    </Page>
  );
};

export default FirmwaresPage;
